/**
 * Email-template metadata for the BU-admin Email Templates page: types,
 * variables, default subject/body, and sample data for the preview.
 * The backend holds the same defaults
 * (functions/python/navigator/domain/service/email_templates.py) — the
 * on_notification_created trigger falls back to them when a BU has no
 * override doc.
 */
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle,
  ClipboardCheck,
  ClipboardList,
  Clock,
  FlaskConical,
  Hourglass,
  MessageCircleReply,
  MessagesSquare,
  Wrench,
  XCircle,
  type LucideIcon,
} from "lucide-react";

/**
 * What a template is *about* — the four conversations a unit's mail belongs to.
 *
 * Fourteen types in one flat row of tabs is more than anyone can scan, and the
 * labels were hidden below a wide viewport, so most of the strip was icons
 * (Jira NAV-98). Grouping is a property of the template rather than of the
 * page: the backend renders these and the page only presents them, so the one
 * place that already knows what each type is for is this table.
 */
export type EmailTemplateGroup =
  /** Somebody asking for a tool the catalogue has, including the three moments
   *  of the routed pipeline (ADR 0017). The largest group, and the one a unit
   *  rewords first. */
  | "ACCESS"
  /** Somebody proposing a tool the catalogue does not have yet. */
  | "EVALUATION"
  /** A request moving, stalling or finishing — addressed to the people running
   *  it rather than to the person who asked. */
  | "PROGRESS"
  /** The clarification loop (PRD D10): a question and its answer. */
  | "CLARIFICATION";

/** The order the groups are shown in: the busiest conversation first, and the
 *  two-template loop last. */
export const EMAIL_TEMPLATE_GROUPS: readonly EmailTemplateGroup[] = [
  "ACCESS",
  "EVALUATION",
  "PROGRESS",
  "CLARIFICATION",
];

export interface EmailTemplateTypeConfig {
  type: string;
  group: EmailTemplateGroup;
  icon: LucideIcon;
  iconColor: string;
  variables: string[];
  defaultSubject: string;
  defaultBody: string;
}

export const EMAIL_TEMPLATE_TYPES: EmailTemplateTypeConfig[] = [
  // ── The routed access pipeline (ADR 0017) ──────────────────────────────
  // Four moments, four readers. Two of those readers may have no Navigator
  // account, so a unit rewording these is rewording the only thing they see.
  // The bodies are the backend's defaults verbatim — the file header explains
  // why the two copies exist, and these were generated from that one.
  {
    type: "ACCESS_REQUEST_NEEDS_APPROVAL",
    group: "ACCESS",
    icon: ClipboardCheck,
    iconColor: "text-amber-500",
    variables: [
      "approverName",
      "businessUnitName",
      "conditionsOfUse",
      "decisionUrl",
      "justification",
      "requesterName",
      "requesterEmail",
      "requesterIdentity",
      "toolName",
    ],
    defaultSubject: "Approval needed: {{toolName}} for {{requesterName}}",
    defaultBody: `<p>Hello {{approverName}},</p><p>{{requesterIdentity}} at {{businessUnitName}} has asked for <strong>{{toolName}}</strong>, and you have been named to decide whether it should go ahead.</p><p>Intended use:<br>"{{justification}}"</p>{{#conditionsOfUse}}<p>Conditions of use, which {{requesterName}} confirmed when asking:<br>"{{conditionsOfUse}}"</p>{{/conditionsOfUse}}<p>Open {{decisionUrl}} and record your decision. Cost is usually the question, but it is not the only one — if you refuse for another reason, say so, because your reason is what {{requesterName}} reads. If you approve, the request goes on to be set up — nobody has access until that has happened.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "ACCESS_REQUEST_READY_TO_SET_UP",
    group: "ACCESS",
    icon: Wrench,
    iconColor: "text-sky-500",
    variables: [
      "approverName",
      "businessUnitName",
      "conditionsOfUse",
      "decisionUrl",
      "justification",
      "requesterName",
      "requesterEmail",
      "requesterIdentity",
      "toolName",
    ],
    defaultSubject: "Ready to set up: {{toolName}} for {{requesterName}}",
    defaultBody: `<p>Hello,</p><p><strong>{{toolName}}</strong> is ready to be set up for {{requesterName}} at {{businessUnitName}}.</p><p>Request details:</p><ul><li>Tool: {{toolName}}</li><li>For: {{requesterIdentity}}</li><li>Intended use: "{{justification}}"</li>{{#conditionsOfUse}}<li>Conditions of use, which {{requesterName}} confirmed when asking: "{{conditionsOfUse}}"</li>{{/conditionsOfUse}}<li>Approved by: {{approverName}}</li></ul><p>Install it or create the account as you normally would, then open {{decisionUrl}} to record what happened — whether you were able to provide it, or could not. {{requesterName}} is told at that point and not before, so the record is what closes this.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "ACCESS_REQUEST_AWAITING_SETUP",
    group: "ACCESS",
    icon: Hourglass,
    iconColor: "text-indigo-500",
    variables: ["approverName", "toolName", "userName"],
    defaultSubject: "Approved, now being set up: {{toolName}}",
    defaultBody: `<p>Hello {{userName}},</p><p>{{approverName}} has approved your request for <strong>{{toolName}}</strong>.</p><p>It is now with the people who set it up. <strong>You do not have access yet</strong> — we will tell you the moment you do.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "REQUEST_SUBMITTED",
    group: "ACCESS",
    icon: ClipboardList,
    iconColor: "text-blue-500",
    variables: [
      "approverName",
      "requesterName",
      "toolName",
      "justification",
      "businessUnitName",
    ],
    defaultSubject: "New Access Request: {{toolName}}",
    defaultBody: `<p>Hello {{approverName}},</p><p>{{requesterName}} has requested access to <strong>{{toolName}}</strong>.</p><p>Intended use:<br>"{{justification}}"</p><p>Please log in to Holtzbrinck Navigator to review this request.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "REQUEST_APPROVED",
    group: "ACCESS",
    icon: CheckCircle,
    iconColor: "text-green-500",
    variables: ["userName", "toolName", "approverName", "businessUnitName"],
    defaultSubject: "Access Approved: {{toolName}}",
    defaultBody: `<p>Hello {{userName}},</p><p>Great news! Your access request for <strong>{{toolName}}</strong> has been approved by {{approverName}}.</p><p>You can now access this tool through Holtzbrinck Navigator.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "REQUEST_DENIED",
    group: "ACCESS",
    icon: XCircle,
    iconColor: "text-red-500",
    variables: [
      "userName",
      "toolName",
      "reason",
      "deniedByName",
      "businessUnitName",
      "refusalContext",
    ],
    defaultSubject: "Access Request Denied: {{toolName}}",
    defaultBody: `<p>Hello {{userName}},</p><p>We regret to inform you that your access request for <strong>{{toolName}}</strong> has been denied by {{deniedByName}}. {{refusalContext}}</p><p>Reason: {{reason}}</p><p>If you believe this was in error, please contact your BU administrator.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  // The evaluation trio: same three moments as the access trio above, for a
  // tool that is not in the catalogue yet. Same variables, so a BU can move
  // wording between them freely.
  {
    type: "EVALUATION_SUBMITTED",
    group: "EVALUATION",
    icon: FlaskConical,
    iconColor: "text-blue-500",
    variables: [
      "approverName",
      "requesterName",
      "toolName",
      "justification",
      "businessUnitName",
    ],
    defaultSubject: "New Tool Evaluation: {{toolName}}",
    defaultBody: `<p>Hello {{approverName}},</p><p>{{requesterName}} has proposed <strong>{{toolName}}</strong> for evaluation — it is not in the {{businessUnitName}} catalogue yet.</p><p>Intended use:<br>"{{justification}}"</p><p>Please log in to Holtzbrinck Navigator to start the evaluation.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "EVALUATION_APPROVED",
    group: "EVALUATION",
    icon: CheckCircle,
    iconColor: "text-emerald-500",
    variables: ["userName", "toolName", "approverName", "businessUnitName"],
    defaultSubject: "Evaluation Approved: {{toolName}}",
    defaultBody: `<p>Hello {{userName}},</p><p>Good news! Your proposal to evaluate <strong>{{toolName}}</strong> has been approved by {{approverName}}.</p><p>The tool is being added to the {{businessUnitName}} catalogue, and your access will appear in Holtzbrinck Navigator.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "EVALUATION_DENIED",
    group: "EVALUATION",
    icon: XCircle,
    iconColor: "text-rose-500",
    variables: [
      "userName",
      "toolName",
      "reason",
      "deniedByName",
      "businessUnitName",
    ],
    defaultSubject: "Evaluation Not Approved: {{toolName}}",
    defaultBody: `<p>Hello {{userName}},</p><p>Your proposal to evaluate <strong>{{toolName}}</strong> was not approved by {{deniedByName}}.</p><p>Reason: {{reason}}</p><p>If you would like to discuss this, please contact your BU administrator.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "STAGE_REVIEW_REQUIRED",
    group: "PROGRESS",
    icon: ArrowUpRight,
    iconColor: "text-purple-500",
    variables: [
      "approverName",
      "requesterName",
      "toolName",
      "stageName",
      "stepUrl",
      "previousStageName",
      "businessUnitName",
    ],
    defaultSubject: "Stage Review Required: {{toolName}} - {{stageName}}",
    defaultBody: `<p>Hello {{approverName}},</p><p>Your review is now required for the <strong>{{stageName}}</strong> stage.</p><p>Request details:</p><ul><li>Tool: {{toolName}}</li><li>Requester: {{requesterName}}</li><li>Current Stage: {{stageName}}</li></ul><p>Open {{stepUrl}} to record what you found.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "ESCALATION",
    group: "PROGRESS",
    icon: AlertTriangle,
    iconColor: "text-amber-500",
    variables: [
      "escalateeName",
      "requesterName",
      "toolName",
      "originalApproverName",
      "hoursOverdue",
      "businessUnitName",
    ],
    defaultSubject:
      "[Escalation] Access Request Requires Attention: {{toolName}}",
    defaultBody: `<p>Hello {{escalateeName}},</p><p>An access request requires your attention. The original approver ({{originalApproverName}}) has not responded within the SLA period.</p><p>Request details:</p><ul><li>Tool: {{toolName}}</li><li>Requester: {{requesterName}}</li><li>Overdue by: {{hoursOverdue}} hours</li></ul><p>Please log in to Holtzbrinck Navigator to review this request.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "REQUEST_COMPLETED",
    group: "PROGRESS",
    icon: Clock,
    iconColor: "text-gray-500",
    variables: [
      "buAdminName",
      "requesterName",
      "toolName",
      "outcome",
      "businessUnitName",
    ],
    defaultSubject: "Request {{outcome}}: {{toolName}}",
    defaultBody: `<p>Hello {{buAdminName}},</p><p>An access request workflow has completed.</p><p>Details:</p><ul><li>Tool: {{toolName}}</li><li>Requester: {{requesterName}}</li><li>Outcome: {{outcome}}</li></ul><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  // The clarification loop (PRD D10). Both bodies quote the message on
  // purpose: a mail saying only "you have a question waiting" makes signing in
  // the price of finding out whether it matters, which is the friction the
  // in-app exchange exists to remove.
  {
    type: "CLARIFICATION_REQUESTED",
    group: "CLARIFICATION",
    icon: MessagesSquare,
    iconColor: "text-sky-500",
    variables: [
      "userName",
      "authorName",
      "toolName",
      "message",
      "businessUnitName",
    ],
    defaultSubject: "A question about your request: {{toolName}}",
    defaultBody: `<p>Hello {{userName}},</p><p>{{authorName}} asked you a question about your request for <strong>{{toolName}}</strong>:</p><blockquote>{{message}}</blockquote><p>Please log in to Holtzbrinck Navigator and answer on the request itself, so your answer is recorded with it.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
  {
    type: "CLARIFICATION_ANSWERED",
    group: "CLARIFICATION",
    icon: MessageCircleReply,
    iconColor: "text-teal-500",
    variables: [
      "recipientName",
      "authorName",
      "toolName",
      "message",
      "businessUnitName",
    ],
    defaultSubject: "{{authorName}} answered your question: {{toolName}}",
    defaultBody: `<p>Hello {{recipientName}},</p><p>{{authorName}} answered on the request for <strong>{{toolName}}</strong>:</p><blockquote>{{message}}</blockquote><p>Please log in to Holtzbrinck Navigator to continue.</p><p>Best regards,<br>Holtzbrinck Navigator</p>`,
  },
];

/**
 * The templates of each group, in the groups' own order.
 *
 * Derived rather than written out a second time: a list of groups holding lists
 * of types would let a template be listed twice or not at all, and one that
 * appears nowhere is a template no admin can reach. Empty groups are dropped —
 * a heading over nothing reads as a loading failure.
 */
export function emailTemplatesByGroup(): {
  group: EmailTemplateGroup;
  templates: EmailTemplateTypeConfig[];
}[] {
  return EMAIL_TEMPLATE_GROUPS.map((group) => ({
    group,
    templates: EMAIL_TEMPLATE_TYPES.filter((config) => config.group === group),
  })).filter((entry) => entry.templates.length > 0);
}

/** Preview sample values (source SAMPLE_DATA, verbatim). */
export const SAMPLE_DATA: Record<string, string> = {
  refusalContext:
    "The request was approved, but the access could not be set up.",
  decisionUrl:
    "https://navigator.app.holtzbrinck.com/end-user/decisions/NV-2026-1F4A",
  approverName: "John Smith",
  requesterName: "Jane Doe",
  requesterEmail: "jane.doe@macmillan.com",
  requesterIdentity: "Jane Doe (jane.doe@macmillan.com)",
  userName: "Jane Doe",
  toolName: "ChatGPT Enterprise",
  justification: "I need this tool for content creation and research tasks.",
  conditionsOfUse: "Browser only. Do not connect it to your work email.",
  businessUnitName: "Macmillan Publishers",
  reason: "This tool has not been approved for use in our business unit yet.",
  deniedByName: "John Smith",
  stageName: "Legal Review",
  previousStageName: "Security Review",
  escalateeName: "Sarah Johnson",
  originalApproverName: "John Smith",
  hoursOverdue: "48",
  buAdminName: "Admin User",
  outcome: "Approved",
  authorName: "John Smith",
  recipientName: "John Smith",
  message: "Could you send us the signed data-processing agreement?",
  stepUrl:
    "https://navigator.app.holtzbrinck.com/end-user/waiting-on-you?tab=steps&request=abc123",
};

/** {{name}} substitution; unprovided placeholders stay literal (source
 *  replaceVariables behavior, same as the backend renderer).
 *
 *  One conditional, as the backend has: `{{#name}}…{{/name}}` keeps its
 *  contents when `name` has a non-blank value and drops them otherwise, so a
 *  line about something a request does not carry is left out rather than
 *  printed empty (Jira NAV-255). Sections do not nest; a tag left over from a
 *  nested or unclosed one is removed rather than printed. */
export function replaceVariables(
  template: string,
  variables: Record<string, string>,
): string {
  return template
    .replace(
      /\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g,
      (_match, name: string, body: string) =>
        (variables[name] ?? "").trim() ? body : "",
    )
    .replace(/\{\{[#/]\w+\}\}/g, "")
    .replace(
      /\{\{(\w+)\}\}/g,
      (match, name: string) => variables[name] ?? match,
    );
}
